import { Dispatch, forwardRef, ReactNode, SetStateAction } from 'react'
import './Input.css'
import { useTranslation } from 'react-i18next'
import Svgs from 'assets/svgs'
import { smoothScrollTo } from 'common/scroll'

export interface InputInfoMsg {
    msg: string,
    type: "passed" | "error"
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    inputVal: string
    setInputVal?: Dispatch<SetStateAction<string>>
    title?: string
    infoMsg?: InputInfoMsg
    isRequired?: boolean
    notShowInfo? : boolean
    containerStyle?: React.CSSProperties
    showClearButton?: boolean
    children?: ReactNode
}


export const Input = forwardRef<HTMLInputElement, InputProps>(({
    title,
    infoMsg,
    inputVal,
    setInputVal,
    isRequired,
    notShowInfo,
    containerStyle,
    showClearButton = true,
    children,
    className,
    ...rest
}, ref) => {
    const {t} = useTranslation();

    const handleClear = () => {
        setInputVal?.('')
    }

    return (
        <div style={containerStyle ?? {padding: "10px 0"}}>
            {title &&
                <div className="common-input-title-container">
                    <span className="common-input-title">{title}</span>
                    {isRequired &&
                        <span className="common-input-required">{t("required")}</span>
                    }
                </div>
            }
            {children ? (
                <div className="common-input-multi-wrapper">
                    {children}
                </div>
            ) : (
                <div className="common-input-wrapper">
                    <input
                        ref={ref}
                        className={`common-input ${className || ""}`}
                        {...rest}
                        value={inputVal}
                        onFocus={(e) => {
                            const target = e.target;
                            const vv = window.visualViewport;
                            let settled = false;
                            let debounceTimer: ReturnType<typeof setTimeout>;

                            const scrollToInput = () => {
                                if (settled) return;
                                settled = true;
                                vv?.removeEventListener('resize', onResize);

                                if (document.activeElement !== target) return;
                                requestAnimationFrame(() => {
                                    const container = target.closest('.common-input-wrapper')?.parentElement;
                                    const containerRect = (container || target).getBoundingClientRect();

                                    const headerEl = document.querySelector('.header_wrapper') as HTMLElement;
                                    const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

                                    const stepEl = target.closest('[class^="sign-up-step"]');
                                    const bottomBtnEl = stepEl?.querySelector(':scope > div:last-child') as HTMLElement;
                                    const visibleHeight = vv?.height ?? window.innerHeight;
                                    const visibleBottom = visibleHeight - (bottomBtnEl?.offsetHeight ?? 0);

                                    if (containerRect.top < headerBottom) {
                                        smoothScrollTo(Math.max(0, window.scrollY + containerRect.top - headerBottom));
                                    } else if (containerRect.bottom > visibleBottom) {
                                        smoothScrollTo(window.scrollY + containerRect.bottom - visibleBottom);
                                    }
                                });
                            };

                            // 키보드 resize가 멈출 때까지 디바운스 후 스크롤
                            const onResize = () => {
                                clearTimeout(debounceTimer);
                                debounceTimer = setTimeout(scrollToInput, 150);
                            };
                            vv?.addEventListener('resize', onResize);
                            // Fallback: 키보드가 이미 열려있어 resize가 발생하지 않는 경우
                            setTimeout(scrollToInput, 300);
                            rest.onFocus?.(e);
                        }}
                        onBlur={(e) => {
                            rest.onBlur?.(e);
                        }}
                        onChange={(e) => {
                            setInputVal && setInputVal(e.target.value)
                            rest.onChange?.(e)
                        }}
                    />
                    {showClearButton && inputVal && (
                        <button
                            type="button"
                            className="common-input-clear-btn"
                            onClick={handleClear}
                            tabIndex={-1}
                        >
                            <Svgs.IcClearButton36 height="20px" width="20px" style={{color: 'var(--lightGray3'}}/>
                        </button>
                    )}
                </div>
            )}
            {!notShowInfo &&
                <div className="common-input-msg-wrapper">
                    {infoMsg &&
                        <>
                            {infoMsg.type === "error" &&
                                <Svgs.IcError
                                    height="12px"
                                    width="12px"
                                    color="var(--warning)"
                                />
                            }
                            {infoMsg.type === "passed" &&
                                <Svgs.IcPassed
                                    height="12px"
                                    width="12px"
                                    color="var(--primary)"
                                />
                            }
                            <span className={`common-input-msg ${infoMsg.type}`}>{infoMsg.msg}</span>
                        </>
                    }
                </div>
            }

        </div>
    )
});
